import { useEffect } from "react";

/**
 * Per-page <title>, meta description, canonical and robots for the SPA
 * routes (docs/features/seo-geo.md Rule 13). Prerendered pages ship their own
 * head for crawlers; this keeps the tab title and shared links right once the
 * app takes over, and marks non-content pages (404, staff) noindex.
 *
 * On unmount the previous values come back, so a page never leaks its title
 * into the next one that sets none.
 */
export interface PageMeta {
  title: string;
  description?: string;
  /** Absolute URL; omitted = leave the current canonical alone. */
  canonical?: string;
  noindex?: boolean;
}

function headEl<T extends HTMLElement>(selector: string, create: () => T): T {
  const found = document.head.querySelector<T>(selector);
  if (found) return found;
  const el = create();
  document.head.appendChild(el);
  return el;
}

export function usePageMeta(meta: PageMeta | null): void {
  const { title, description, canonical, noindex } = meta ?? {};
  useEffect(() => {
    if (!title) return;
    const prevTitle = document.title;
    document.title = title;

    const desc = headEl("meta[name='description']", () =>
      Object.assign(document.createElement("meta"), { name: "description" }),
    );
    const prevDesc = desc.content;
    if (description) desc.content = description;

    const link = headEl("link[rel='canonical']", () =>
      Object.assign(document.createElement("link"), { rel: "canonical" }),
    );
    const prevCanonical = link.href;
    if (canonical) link.href = canonical;

    const robots = noindex
      ? headEl("meta[name='robots']", () => Object.assign(document.createElement("meta"), { name: "robots" }))
      : null;
    const prevRobots = robots?.content ?? "";
    if (robots) robots.content = "noindex";

    return () => {
      document.title = prevTitle;
      desc.content = prevDesc;
      if (prevCanonical) link.href = prevCanonical;
      if (robots) {
        if (prevRobots) robots.content = prevRobots;
        else robots.remove();
      }
    };
  }, [title, description, canonical, noindex]);
}

/** Absolute URL on the production origin (canonicals are always restonow.app). */
export function siteUrl(path: string): string {
  return `https://restonow.app${path}`;
}
